// Theme management utility
export const THEME_KEY = 'theme';
export const THEME_LIGHT = 'light';
export const THEME_DARK = 'dark';

/**
 * Apply theme to document element
 * @param {string} theme - 'light' or 'dark'
 */
export const applyTheme = (theme) => {
  if (theme === THEME_DARK) {
    document.documentElement.classList.add(THEME_DARK);
  } else {
    document.documentElement.classList.remove(THEME_DARK);
  }
};

/**
 * Get saved theme from localStorage
 * @returns {string} - 'light' or 'dark'
 */
export const getSavedTheme = () => {
  return localStorage.getItem(THEME_KEY) || THEME_LIGHT;
};

/**
 * Save theme to localStorage and apply it
 * @param {string} theme - 'light' or 'dark'
 */
export const setTheme = (theme) => {
  localStorage.setItem(THEME_KEY, theme);
  applyTheme(theme);
};

/**
 * Toggle between light and dark theme
 * @returns {string} - The new theme that was applied
 */
export const toggleTheme = () => {
  const currentTheme = getSavedTheme();
  const newTheme = currentTheme === THEME_DARK ? THEME_LIGHT : THEME_DARK;
  setTheme(newTheme);
  return newTheme;
};

/**
 * Initialize theme on app startup
 * Call this before React renders to prevent flash
 */
export const initializeTheme = () => {
  const savedTheme = getSavedTheme();
  applyTheme(savedTheme);
  return savedTheme;
};

/**
 * Listen for theme changes from other tabs/windows
 * @param {Function} callback - Function to call when theme changes
 * @returns {Function} - Cleanup function to remove event listener
 */
export const listenForThemeChanges = (callback) => {
  const handleStorageChange = (e) => {
    if (e.key === THEME_KEY) {
      const newTheme = e.newValue || THEME_LIGHT;
      applyTheme(newTheme);
      callback?.(newTheme);
    }
  };

  window.addEventListener('storage', handleStorageChange);
  return () => window.removeEventListener('storage', handleStorageChange);
};
